import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { getAccessToken, setAccessToken, onSessionExpired } from "../lib/api";
import { isEmbedded, resolvePortalOrigin, requestUserInfoFromParent } from "../lib/integration";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged
  const [ready, setReady] = useState(false);
  const [embedError, setEmbedError] = useState("");
  const navigate = useNavigate();
  const pending = useRef(null);
  const openedHome = useRef(false);

  const connectEmbedded = useCallback(() => {
    if (pending.current) return pending.current;
    pending.current = (async () => {
      try {
        const origin = resolvePortalOrigin();
        if (!origin) throw new Error("Portail parent non autorisé");
        const { assertion } = await requestUserInfoFromParent(origin);
        const { data } = await api.post("/integration/sessions", { assertion }, { _renewed: true });
        setAccessToken(data.access_token);
        setUser(data.user);
        setEmbedError("");
        if (data.active_org_id && !openedHome.current) {
          openedHome.current = true;
          navigate(`/admin/org/${data.active_org_id}`, { replace: true });
        }
      } catch (e) {
        setAccessToken(null);
        setUser(false);
        setEmbedError(e.response?.data?.detail || e.message || "Connexion impossible");
        throw e;
      } finally {
        setReady(true);
        pending.current = null;
      }
    })();
    return pending.current;
  }, [navigate]);

  useEffect(() => {
    if (isEmbedded) {
      onSessionExpired(connectEmbedded);
      return;
    }
    if (!getAccessToken()) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        setAccessToken(null);
        setUser(false);
      })
      .finally(() => setReady(true));
  }, [connectEmbedded]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    setAccessToken(null);
    setUser(false);
  };

  const startEmbedded = useCallback(() => {
    setEmbedError("");
    connectEmbedded().catch(() => {});
  }, [connectEmbedded]);

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, embedError, startEmbedded }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
